import { type ComputedRef, type Ref, ref, computed } from "vue";

import apiGraph, { graphGet } from "@/helpers/api/apiGraph";

import DriveItem from "@/models/Documents/DriveItem";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";
import Permission from "@/models/Documents/Permission";

import FileHelper from "@/helpers/utilities/FileHelper";

import { useFeaturesStore } from "@/stores/features";
import { useSectionsStore } from "@/stores/sections";
import { useMeStore } from "@/stores/me";

import apiSkobo from "@/helpers/api/apiSkobo";

interface LegacyDriveItem {
  id: string;
  name?: string;
  parentReference: { driveId: string; id?: string };
  children?: Array<{ id: string; name?: string; folder?: unknown; [key: string]: unknown }>;
  [key: string]: unknown;
}

interface LegacyComposableDependencies {
  // myDrive
  calculateDriveIdOrMyDrive: ComputedRef<(driveId: string | null | undefined) => string | null | undefined>;
  myDriveRootId: ComputedRef<string | null>;

  // permissions
  myItemPermission: ComputedRef<(driveId: string, itemId: string) => Permission | null>;
  sharedWithPermission: ComputedRef<(driveId: string, itemId: string) => Record<string, Permission>>;
  reloadDocumentPermissions: (driveId: string, itemId: string) => Promise<Permission>;
  reloadSharedWithPermissions: (driveId: string, itemId: string) => Promise<void>;

  // driveItems
  loadDriveItem: (
    driveId: string,
    itemId: string,
    additionalProperties?: Record<string, unknown>
  ) => Promise<DriveItem>;

  // Lazy: calculateRootOrItemId remains in index.ts (depends on this composable's
  // driveRootItemIdMap, so it can't be passed in directly)
  getCalculateRootOrItemId: () => (driveId: string, itemId: string) => string;
}

export const useLegacyComposable = (dependencies: LegacyComposableDependencies) => {
  const legacyDriveMap: Ref<Record<string, Record<string, DriveItem>>> = ref({});
  const legacyDriveBreadCrumbMap: Ref<Record<string, Record<string, DriveItem>>> = ref({});
  const driveRootItemIdMap: Ref<Record<string, string>> = ref({});
  const driveRootDisplayNameMap: Ref<Record<string, string>> = ref({});
  const sectionRootDriveSettingMap: Ref<Record<string, { driveId: string; itemId: string }>> = ref({});
  const searchSetMap: Ref<Record<string, { searchTerm: string; itemId: string; searchResults: DriveItem[] }>> = ref({});

  const calculateRootOrDriveIdLegacy = computed(
    () =>
      ({ driveId }: { driveId: string | null | undefined }) =>
        dependencies.calculateDriveIdOrMyDrive.value(driveId)
  );

  const calculateRootOrItemIdLegacy = computed(
    () =>
      ({ driveId, itemId }: { driveId: string; itemId: string | null | undefined }) => {
        if (itemId) {
          return itemId;
        }
        for (const section in sectionRootDriveSettingMap.value) {
          if (sectionRootDriveSettingMap.value[section].driveId === driveId) {
            return sectionRootDriveSettingMap.value[section].itemId;
          }
        }
        const calculateRootOrItemId = dependencies.getCalculateRootOrItemId();
        return calculateRootOrItemId(driveId, itemId as string);
      }
  );

  const myDriveRoot = computed(() => dependencies.myDriveRootId.value);

  const sectionDriveRootSetting = computed(
    () => (sectionId: string) =>
      sectionRootDriveSettingMap.value[sectionId] ? sectionRootDriveSettingMap.value[sectionId] : {}
  );

  const driveRootSection = computed(
    () => (driveId: string) =>
      Object.keys(sectionRootDriveSettingMap.value).find(
        key => sectionRootDriveSettingMap.value[key].driveId === driveId
      )
  );

  const legacyDriveItemFromMap = computed(
    () =>
      ({ driveId, itemId }: { driveId: string | null | undefined; itemId: string | null | undefined }) => {
        try {
          const calcDriveId = calculateRootOrDriveIdLegacy.value({ driveId });
          const calcItemId = calculateRootOrItemIdLegacy.value({ driveId: calcDriveId as string, itemId });

          if (legacyDriveMap.value[calcDriveId as string]) {
            if (legacyDriveMap.value[calcDriveId as string][calcItemId]) {
              return legacyDriveMap.value[calcDriveId as string][calcItemId];
            } else {
              for (const key in legacyDriveMap.value[calcDriveId as string]) {
                const item = legacyDriveMap.value[calcDriveId as string][key] as DriveItem & {
                  children?: DriveItem[];
                };
                if (item.children) {
                  const foundChild = item.children.find((child: DriveItem) => child.id === calcItemId);
                  if (foundChild) {
                    return foundChild;
                  }
                }
              }
            }
          }
          // eslint-disable-next-line no-empty
        } catch {}
        return null;
      }
  );

  const sectionDriveRootDriveItem = computed(() => (sectionId: string) => {
    const sectionDriveRoot = sectionRootDriveSettingMap.value[sectionId];
    if (!sectionDriveRoot) {
      return null;
    }
    const fromMap = legacyDriveItemFromMap.value({
      driveId: sectionDriveRoot.driveId,
      itemId: sectionDriveRoot.itemId
    });
    if (!fromMap) {
      return null;
    }
    const driveItem = JSON.parse(JSON.stringify(fromMap));
    const sectionsStore = useSectionsStore();
    const subDrives = sectionsStore.sectionSubDrives(sectionDriveRoot.driveId);
    if (subDrives) {
      driveItem.children = driveItem.children || [];
      driveItem.children.push(...subDrives);
    }
    return driveItem;
  });

  const legacyDriveItem = computed(
    () =>
      ({ driveId, itemId }: { driveId: string | null | undefined; itemId: string | null | undefined }) => {
        const driveItem = legacyDriveItemFromMap.value({ driveId, itemId });
        if (driveItem && driveItem.name === "root") {
          const sectionForDriveId = driveRootSection.value(driveId as string);
          if (sectionForDriveId) {
            return sectionDriveRootDriveItem.value(sectionForDriveId);
          }
        }
        return driveItem;
      }
  );

  const legacyDriveItemBreadcrumb = computed(
    () =>
      ({ driveId, itemId }: { driveId: string | null | undefined; itemId: string | null | undefined }) => {
        const calcDriveId = calculateRootOrDriveIdLegacy.value({ driveId });
        const calcItemId = calculateRootOrItemIdLegacy.value({ driveId: calcDriveId as string, itemId });
        if (legacyDriveBreadCrumbMap.value[calcDriveId as string]) {
          return legacyDriveBreadCrumbMap.value[calcDriveId as string][calcItemId];
        }
        return null;
      }
  );

  const legacyDriveItemChildren = computed(
    () =>
      ({ driveId, itemId }: { driveId: string | null | undefined; itemId: string | null | undefined }) => {
        const driveItem = legacyDriveItem.value({ driveId, itemId });
        if (driveItem && driveItem.children) {
          return driveItem.children;
        }
        return null;
      }
  );

  const legacyDriveItemChildrenFolders = computed(
    () =>
      ({ driveId, itemId }: { driveId: string | null | undefined; itemId: string | null | undefined }) => {
        const calcDriveId = calculateRootOrDriveIdLegacy.value({ driveId });
        const calcItemId = calculateRootOrItemIdLegacy.value({ driveId: calcDriveId as string, itemId });
        const driveItem = legacyDriveItem.value({ driveId: calcDriveId as string, itemId: calcItemId }) as
          | (DriveItem & { children?: Array<DriveItem & { permissions?: Permission }> })
          | null;
        if (driveItem && driveItem.children) {
          const folders = driveItem.children.filter(child => Object.prototype.hasOwnProperty.call(child, "folder"));
          folders.map(folder => {
            folder.permissions = dependencies.myItemPermission.value(calcDriveId as string, folder.id) ?? undefined;
          });
          return folders;
        }
        return null;
      }
  );

  const permissionObjectsForPersonListOnDriveItem = computed(
    () =>
      ({
        persons,
        driveId,
        itemId
      }: {
        persons: Array<{
          userId: string;
          extSysApiUserId: string;
          emailSchoolAddress: string;
          name?: { name: string };
        }>;
        driveId: string;
        itemId: string;
      }) => {
        const permissionForOthers: Record<string, Permission> = dependencies.sharedWithPermission.value(
          driveId,
          itemId
        );

        return persons.map(person => {
          if (permissionForOthers[person.extSysApiUserId]) {
            return {
              userId: person.userId,
              extSysApiUserId: person.extSysApiUserId,
              emailSchoolAddress: person.emailSchoolAddress,
              label: person.name ? person.name.name : "",
              permissionOnItem: permissionForOthers[person.extSysApiUserId],
              personAvatar: person
            };
          } else if (permissionForOthers[person.emailSchoolAddress]) {
            return {
              userId: person.userId,
              extSysApiUserId: person.extSysApiUserId,
              emailSchoolAddress: person.emailSchoolAddress,
              label: person.name ? person.name.name : "",
              permissionOnItem: permissionForOthers[person.emailSchoolAddress],
              personAvatar: person
            };
          } else {
            return {
              userId: person.userId,
              extSysApiUserId: person.extSysApiUserId,
              emailSchoolAddress: person.emailSchoolAddress,
              label: person.name ? person.name.name : "",
              permissionOnItem: new Permission(),
              personAvatar: person
            };
          }
        });
      }
  );

  const searchTerm = computed(() => (driveId?: string) => {
    const driveIdOrWorld = driveId || "world";
    return searchSetMap.value[driveIdOrWorld] ? searchSetMap.value[driveIdOrWorld].searchTerm : "";
  });

  const searchItemId = computed(() => (driveId?: string) => {
    const driveIdOrWorld = driveId || "world";
    return searchSetMap.value[driveIdOrWorld] ? searchSetMap.value[driveIdOrWorld].itemId : null;
  });

  const searchResults = computed(() => (driveId?: string) => {
    const driveIdOrWorld = driveId || "world";
    return searchSetMap.value[driveIdOrWorld] ? searchSetMap.value[driveIdOrWorld].searchResults : [];
  });

  const mutateLoadDrive = (data: DriveItem & { children?: DriveItem[] }) => {
    (data as DriveItem & { extension: unknown }).extension = {
      name: "Folder",
      icon: ["fas", "folder"],
      color: "#ffda6a"
    };
    (data as DriveItem & { isFolder?: boolean }).isFolder = true;
    data.children = FileHelper.addExtensionsToArrayOfFiles(data.children);
    data.children = data.children!.map((child: DriveItem) => {
      (child as DriveItem & { isFolder?: boolean }).isFolder = Object.prototype.hasOwnProperty.call(child, "folder");
      return child;
    });

    if (!legacyDriveMap.value[data.parentReference.driveId]) {
      legacyDriveMap.value[data.parentReference.driveId] = {};
    }
    legacyDriveMap.value[data.parentReference.driveId][data.id] = data;
    driveRootItemIdMap.value[data.parentReference.driveId] = data.id;
  };

  const loadLegacyDrive = async (driveId: string) => {
    try {
      const result = await graphGet(`/drives/${driveId}/root/?$expand=children`);
      const featureStore = useFeaturesStore();
      if (featureStore.isFeatureEnabled("DocumentSensitivityLabels")) {
        const responseChildren = await apiGraph.get(
          `/drives/${driveId}/root/children?$expand=listItem($expand=fields($select=_DisplayName))`
        );
        result.children = responseChildren.data.value;
      }
      mutateLoadDrive(result);
      return result;
    } catch {
      return null;
    }
  };

  const loadDocuments = async ({ driveId, itemId }: { driveId: string; itemId?: string }) => {
    if (itemId) {
      return dependencies.loadDriveItem(driveId, itemId);
    } else {
      return await loadLegacyDrive(driveId);
    }
  };

  const setLegacyDriveRoot = ({
    driveId,
    itemId,
    displayName
  }: {
    driveId: string;
    itemId: string;
    displayName?: string;
  }) => {
    driveRootItemIdMap.value[driveId] = itemId;
    if (displayName !== undefined) {
      driveRootDisplayNameMap.value[driveId] = displayName;
    }
  };

  const fetchWebUrl = async ({ driveId, itemId }: { driveId: string; itemId: string }) => {
    const response = await apiGraph.get(`/drives/${driveId}/items/${itemId}`);
    return response.data.webUrl;
  };

  const legacyRemoveSharing = async ({
    driveId,
    itemId,
    sharingPermissionIds,
    doNotReload
  }: {
    driveId: string;
    itemId: string;
    sharingPermissionIds: string[];
    doNotReload?: boolean;
  }) => {
    try {
      const promises = sharingPermissionIds.map(async (id: string) => {
        if (id) {
          return apiGraph
            .delete(`/drives/${driveId}/items/${DriveItemHelper.getItemId(itemId)}/permissions/${id}`)
            .finally(async () => {
              if (!doNotReload) {
                await dependencies.reloadDocumentPermissions(driveId, itemId);
                await dependencies.reloadSharedWithPermissions(driveId, itemId);
              }
            });
        }
      });
      await Promise.all(promises);
      // eslint-disable-next-line no-empty
    } catch {}
  };

  const legacyShareToPeople = async ({
    driveId,
    itemId,
    people,
    write
  }: {
    driveId: string;
    itemId: string;
    people: Array<{ emailSchoolAddress?: string; [key: string]: unknown }>;
    write: boolean;
  }) => {
    const peoplePermissions = permissionObjectsForPersonListOnDriveItem.value({
      persons: people as Array<{
        userId: string;
        extSysApiUserId: string;
        emailSchoolAddress: string;
        name?: { name: string };
      }>,
      driveId,
      itemId
    });

    const permissionIdsToBeRemoved = peoplePermissions
      .filter(
        (entry: { permissionOnItem: { myPermission: number; id: string } }) =>
          entry.permissionOnItem.myPermission !== Permission.nctPermission.NONE
      )
      .map((entry: { permissionOnItem: { myPermission: number; id: string } }) => entry.permissionOnItem.id);

    await legacyRemoveSharing({
      driveId,
      itemId,
      sharingPermissionIds: [...permissionIdsToBeRemoved],
      doNotReload: true
    });

    const recipients = people.map(person => ({ email: person.emailSchoolAddress }));

    await apiGraph.post(`/drives/${driveId}/items/${DriveItemHelper.getItemId(itemId)}/invite`, {
      requireSignIn: true,
      sendInvitation: false,
      roles: write ? ["write"] : ["read"],
      recipients: recipients
    });

    const meStore = useMeStore();
    await Promise.allSettled(
      people.map(person =>
        apiSkobo.post("/Onedrive/SharedWithMe", {
          graphDriveId: driveId,
          graphItemId: DriveItemHelper.getItemId(itemId),
          sharedWithUserId: (person as { userId?: string }).userId,
          sharedByUserId: meStore.userId
        })
      )
    );

    await dependencies.reloadDocumentPermissions(driveId, itemId);
    await dependencies.reloadSharedWithPermissions(driveId, itemId);
  };

  const loadSectionDrivesRoot = async ({ sectionId }: { sectionId: string }) => {
    const sectionsStore = useSectionsStore();
    const section = sectionsStore.sectionWithArchivedFallback(sectionId);

    const response = await apiGraph.get(
      `/groups/${section?.extSysApiSectionId}/drive/items/root:/General/?$expand=children`
    );

    const sectionDriveItem = response.data;
    sectionDriveItem.name = "root";
    delete sectionDriveItem.parentReference.id;

    mutateLoadDrive(sectionDriveItem);
    sectionRootDriveSettingMap.value[sectionId] = {
      driveId: sectionDriveItem.parentReference.driveId,
      itemId: sectionDriveItem.id
    };
  };

  const legacyLoadDriveItem = (driveItem: LegacyDriveItem) => {
    if (!legacyDriveMap.value[driveItem.parentReference.driveId]) {
      legacyDriveMap.value[driveItem.parentReference.driveId] = {};
    }
    legacyDriveMap.value[driveItem.parentReference.driveId][driveItem.id] = driveItem as unknown as DriveItem;
  };

  const legacyLoadBreadCrumbDriveItem = ({ data }: { data: LegacyDriveItem }) => {
    if (!legacyDriveBreadCrumbMap.value[data.parentReference.driveId]) {
      legacyDriveBreadCrumbMap.value[data.parentReference.driveId] = {};
    }
    legacyDriveBreadCrumbMap.value[data.parentReference.driveId][data.id] = data as unknown as DriveItem;
  };

  const legacySearch = ({
    driveId,
    itemId,
    searchTerm: searchTermValue,
    searchResults: searchResultsValue
  }: {
    driveId?: string;
    itemId: string;
    searchTerm: string;
    searchResults: unknown[];
  }) => {
    const driveIdOrWorld = driveId || "world";
    searchSetMap.value[driveIdOrWorld] = {
      searchTerm: searchTermValue,
      itemId,
      searchResults: searchResultsValue as DriveItem[]
    };
  };

  const resetLegacyDriveCache = () => {
    legacyDriveMap.value = {};
    legacyDriveBreadCrumbMap.value = {};
    driveRootItemIdMap.value = {};
    driveRootDisplayNameMap.value = {};
    sectionRootDriveSettingMap.value = {};
    searchSetMap.value = {};
  };

  return {
    legacyDriveMap,
    legacyDriveBreadCrumbMap,
    driveRootItemIdMap,
    driveRootDisplayNameMap,
    sectionRootDriveSettingMap,
    searchSetMap,
    calculateRootOrDriveIdLegacy,
    calculateRootOrItemIdLegacy,
    myDriveRoot,
    sectionDriveRootSetting,
    driveRootSection,
    legacyDriveItemFromMap,
    sectionDriveRootDriveItem,
    legacyDriveItem,
    legacyDriveItemBreadcrumb,
    legacyDriveItemChildren,
    legacyDriveItemChildrenFolders,
    permissionObjectsForPersonListOnDriveItem,
    searchTerm,
    searchItemId,
    searchResults,
    loadDocuments,
    loadLegacyDrive,
    setLegacyDriveRoot,
    fetchWebUrl,
    legacyShareToPeople,
    legacyRemoveSharing,
    loadSectionDrivesRoot,
    legacyLoadDriveItem,
    legacyLoadBreadCrumbDriveItem,
    legacySearch,
    resetLegacyDriveCache
  };
};
